/* eslint-disable @typescript-eslint/camelcase */
import { Course, Lesson, Path, Playlist } from '@fenderdigital/api-definitions/play';

interface ActivityProperties {
  activity_id: string | null;
  activity_slug: string | null;
  activity_title: string | null;
  activity_type_lesson: string | null;
  activity_instrument: string | null;
  activity_genre: string | null;
  activity_level: number | null | undefined;
}

export const getActivityProperties = (
  currentLesson: Lesson | null,
  style: string,
): ActivityProperties => {
  const { id, slug, title, level, type, instrument } = currentLesson || {
    slug: null,
    id: null,
    title: null,
    type: null,
    level: null,
    instrument: null,
  };
  return {
    activity_id: id,
    activity_slug: slug,
    activity_title: title,
    activity_type_lesson: type,
    activity_instrument: instrument,
    activity_genre: style || null,
    activity_level: Number(level),
  };
};

interface NextActivityProperties {
  next_activity_type: string | null;
  next_activity_slug: string | null;
  next_activity_id: string | null;
  next_activity_title: string | null;
}

export const getNextActivityProperties = (nextLesson: Lesson | null): NextActivityProperties => {
  const { id, type, slug, title } = nextLesson || { slug: null, id: null, title: null, type: null };
  return {
    next_activity_type: type,
    next_activity_slug: slug,
    next_activity_id: id,
    next_activity_title: title,
  };
};

export interface CurriculumProperties {
  play_levels_inpath_total: number | null;
  play_courses_inlevel_total: number | null;
  play_activities_incourse_total: number | null;
}

export const getCurriculumProperties = (paths: {
  currentPath: Path;
  currentPlaylist: Playlist;
  currentCourse: Course;
}): CurriculumProperties => {
  const { currentPath, currentPlaylist, currentCourse } = paths;
  return {
    play_levels_inpath_total: currentPath?.playlists.length,
    play_courses_inlevel_total: currentPlaylist?.courses.length,
    play_activities_incourse_total: currentCourse?.lessons.length,
  };
};

interface MyPath extends Path {
  playlists: Playlist[];
}

export interface UserActivitiesCompleted {
  play_levels_inpath_completed: number | null;
  play_courses_inlevel_completed: number | null;
  play_activities_incourse_completed: number | null;
}

interface CurrentPlaylist extends Playlist {
  'lessons-completed-percent': number;
  'current-course': number;
  courses: Course[];
}

export const getUserActivitiesCompleted = (
  myProgress: { paths: any },
  myPath: MyPath,
): UserActivitiesCompleted => {
  const { id, playlists } = myPath;
  const { paths } = myProgress;
  const ActivitiesCompletedProperties = {
    play_levels_inpath_completed: 0,
    play_courses_inlevel_completed: 0,
    play_activities_incourse_completed: 0,
  };

  if (!paths || !playlists || !id || !paths[id] || !paths[id].playlists)
    return ActivitiesCompletedProperties;

  const numberOfLevelsInpath = playlists.length;

  const currentLevelPlaylists: CurrentPlaylist[] = paths[id].playlists;
  const numberOfLevelsCompleted = Object.values(currentLevelPlaylists).filter(
    level => level['lessons-completed-percent'] === 100,
  ).length;
  const levelsInpathCompleted = Math.round(numberOfLevelsCompleted / numberOfLevelsInpath);
  const currentPlaylistId = paths[id].currentPlaylist;
  if (!currentLevelPlaylists[currentPlaylistId]) return ActivitiesCompletedProperties;
  const coursesInlevelCompleted =
    currentLevelPlaylists[currentPlaylistId]['lessons-completed-percent'];
  const currentLessonId = currentLevelPlaylists[currentPlaylistId]['current-course'];
  const coursesInlevel = currentLevelPlaylists[currentPlaylistId].courses;
  const currentLesson = coursesInlevel[currentLessonId];
  if (currentLesson) {
    ActivitiesCompletedProperties.play_activities_incourse_completed =
      currentLevelPlaylists[currentPlaylistId]?.courses[currentLessonId][
        'lessons-completed-percent'
      ] || 0;
  }
  ActivitiesCompletedProperties.play_levels_inpath_completed = Math.round(
    levelsInpathCompleted * 100,
  );
  ActivitiesCompletedProperties.play_courses_inlevel_completed = coursesInlevelCompleted;
  return ActivitiesCompletedProperties;
};

export const isCourseCompleted = (myProgress: { paths: any }, myPath: MyPath): boolean => {
  const { paths } = myProgress;
  const { id } = myPath;

  if (!paths || !id || !paths[id] || !paths[id].playlists) return false;
  const currentLevelPlaylists = paths[id].playlists;
  const currentPlaylistId = paths[id].currentPlaylist;
  const currentCourseProgress = currentLevelPlaylists[currentPlaylistId];
  if (!currentCourseProgress) return false;
  const coursesInlevel = currentCourseProgress.courses;
  const currentLessonId = currentCourseProgress['current-course'];
  const currentLesson = coursesInlevel[currentLessonId];
  if (!currentLesson) return false;
  const courseLessonCompleted = coursesInlevel[currentLessonId]['lessons-completed-percent'];
  const lastActivityTobeCompletedInCourse =
    coursesInlevel[currentLessonId]['lessons-completed'] + 1 ===
    coursesInlevel[currentLessonId]['lessons-total'];
  const courseProgressStatus = courseLessonCompleted === 100 || lastActivityTobeCompletedInCourse;
  return courseProgressStatus;
};
